import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { URLS, Url } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { useVerification } from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import { getSubmitUrlForProfileOwner } from '../../../../utils/urls';
import { GallectionSourceType } from '../../../../../types/gallection';
import { ModuleType } from '../../../../../types/modules';
import EmptyState from '../../../EmptyState';
import { ExhibitionistBoxType } from '../ExhibitionistBox';

import s from './ExhibitionistEmptyState.scss';

const componentLocale = 'widgets.exhibitionist.empty';
const navigateTo = url =>
  typeof window !== 'undefined' && (window as any).IS_STORYBOOK
    ? alert(`navigate to ${url}`)
    : window.location.assign(url);

export interface Props {
  moduleId: number;
  exhibitionistType: ExhibitionistBoxType;
  view: 'large' | 'small' | 'wide';
  sourceType: GallectionSourceType;
  isOwner: boolean;
  profileOwnerUser: PapiUser;
  className?: string;
}

const ExhibitionistEmptyState: React.FC<Props> = ({
  exhibitionistType,
  view,
  sourceType,
  isOwner,
  profileOwnerUser,
  className,
}) => {
  const username = profileOwnerUser.username;
  const { t } = useTranslation();
  const { aiContentFilter } = useContext(SessionContext);
  const { restrictToVerified } = useVerification();

  const isGroupProfile = profileOwnerUser && isGroup(profileOwnerUser);
  const viewer = isOwner ? 'owner' : 'visitor';
  const localePrefix = `${componentLocale}.${sourceType}`;
  const localeUsername = viewer === 'visitor' ? { username } : undefined;
  const title = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.title`
      : `${localePrefix}.${viewer}.title`,
    localeUsername
  );
  const subtitle = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.subtitle`
      : `${localePrefix}.${viewer}.subtitle`,
    localeUsername
  );

  const ownerCTALocale = `${localePrefix}.owner.CTALabel`;

  const extraProps = {};
  switch (sourceType) {
    case GallectionSourceType.COLLECTION:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(URLS.ROOT);
      break;
    case GallectionSourceType.ADOPTABLE_BOUGHT:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(URLS.SHOP);
      break;
    case GallectionSourceType.ADOPTABLE_FOR_SALE:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(URLS.SUBMIT);
      break;
    default:
      extraProps['buttonLabel'] = t(
        isGroupProfile && isOwner
          ? `${componentLocale}.group.CTALabel`
          : ownerCTALocale
      );
      extraProps['onButtonClick'] = restrictToVerified('submit', () =>
        navigateTo(getSubmitUrlForProfileOwner(profileOwnerUser))
      );
      extraProps['buttonVariant'] = 'approval-green';
      extraProps['biData'] = {
        evid: 704,
        sectionname: ModuleType.EXHIBITIONIST,
      };
  }

  return (
    <EmptyState
      view={view}
      className={classnames(className, s[`${exhibitionistType}_${sourceType}`])}
      title={title}
      subtitle={subtitle}
      withButton={isOwner}
      customSecondaryButton={
        aiContentFilter !== 'suppress' && sourceType === GallectionSourceType.FEATURED_GALLERY && (
          <GhostButton
            variant="main"
            size="medium"
            icon={DreamUpIcon}
            href={Url.dreamupLink()}
          >
            {t(`${localePrefix}.owner.dreamupCta.label`)}
          </GhostButton>
        )
      }
      {...extraProps}
    />
  );
};

export default ExhibitionistEmptyState;
